import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { onboardingApi, ApiError } from '@/api/onboarding';
import { Button } from '@/components/atoms';
import type { AuthType } from '@/types/config';

import styles from './ConectarWordPress.module.css';

export interface ConectarWordPressProps {
  initialEmpty?: boolean;
}

export function ConectarWordPress({ initialEmpty = false }: ConectarWordPressProps = {}) {
  const { t } = useTranslation(['funil', 'common']);
  const navigate = useNavigate();

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [siteId, setSiteId] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [restPath, setRestPath] = useState('/wp-json');
  const [authType, setAuthType] = useState<AuthType>('app_password');
  const [authUser, setAuthUser] = useState('');
  const [authSecret, setAuthSecret] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testResultMsg, setTestResultMsg] = useState<string | null>(null);
  const [testSuccess, setTestSuccess] = useState<boolean | null>(null);
  const [isEmpty] = useState(initialEmpty);

  async function handleSalvarECriarSite(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const site = await onboardingApi.createSite({
        name: name.trim(),
        slug: slug.trim(),
        base_url: baseUrl.trim(),
        rest_path: restPath.trim() || '/wp-json',
        auth_type: authType,
        auth_user: authUser.trim() || null,
        auth_secret: authSecret || null,
      });

      setSiteId(site.id);
      setStep(2);
      void executarTeste(site.id);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(t('funil:conectar_wp.erro_criar_site'));
      } else {
        setError(t('funil:conectar_wp.erro_criar_site'));
      }
    } finally {
      setLoading(false);
    }
  }

  async function executarTeste(idParaTestar: string) {
    setLoading(true);
    setError(null);
    setTestResultMsg(null);
    setTestSuccess(null);
    try {
      const res = await onboardingApi.testSite(idParaTestar);
      if (res.ok) {
        setTestSuccess(true);
        setTestResultMsg(res.message || t('funil:conectar_wp.sucesso_teste'));
        setStep(3);
      } else {
        setTestSuccess(false);
        setError(res.message || t('funil:conectar_wp.erro_teste'));
      }
    } catch {
      setTestSuccess(false);
      setError(t('funil:conectar_wp.erro_teste'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.root}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>{t('funil:conectar_wp.titulo')}</h1>
          <p className={styles.subtitle}>{t('funil:conectar_wp.subtitulo')}</p>
        </div>

        <div className={styles.stepper}>
          <div className={step === 1 ? styles.stepActive : styles.stepIndicator}>
            {t('funil:conectar_wp.passo1_titulo')}
          </div>
          <div className={step === 2 ? styles.stepActive : styles.stepIndicator}>
            {t('funil:conectar_wp.passo2_titulo')}
          </div>
          <div className={step === 3 ? styles.stepActive : styles.stepIndicator}>
            {t('funil:conectar_wp.passo3_titulo')}
          </div>
        </div>

        {isEmpty && (
          <div className={styles.emptyCard}>
            <h2>{t('funil:conectar_wp.vazio_titulo')}</h2>
            <p>{t('funil:conectar_wp.vazio_descricao')}</p>
          </div>
        )}

        {error && <div className={styles.errorBox}>{error}</div>}

        {testResultMsg && testSuccess && <div className={styles.successBox}>{testResultMsg}</div>}

        {step === 1 && (
          <form className={styles.card} onSubmit={handleSalvarECriarSite}>
            <h2 className={styles.stepTitle}>{t('funil:conectar_wp.passo1_titulo')}</h2>

            <label className={styles.field}>
              <span>{t('funil:conectar_wp.site_nome')}</span>
              <input value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
            </label>

            <label className={styles.field}>
              <span>{t('funil:conectar_wp.site_slug')}</span>
              <input value={slug} onChange={(e) => setSlug(e.target.value)} required />
            </label>

            <label className={styles.field}>
              <span>{t('funil:conectar_wp.base_url')}</span>
              <input
                type="url"
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                placeholder="https://exemplo.com"
                required
              />
            </label>

            <label className={styles.field}>
              <span>{t('funil:conectar_wp.rest_path')}</span>
              <input value={restPath} onChange={(e) => setRestPath(e.target.value)} />
            </label>

            <label className={styles.field}>
              <span>{t('funil:conectar_wp.auth_type')}</span>
              <select value={authType} onChange={(e) => setAuthType(e.target.value as AuthType)}>
                <option value="app_password">Application Password</option>
                <option value="jwt">JWT</option>
                <option value="basic">Basic Auth</option>
              </select>
            </label>

            <label className={styles.field}>
              <span>{t('funil:conectar_wp.auth_user')}</span>
              <input value={authUser} onChange={(e) => setAuthUser(e.target.value)} />
            </label>

            <label className={styles.field}>
              <span>{t('funil:conectar_wp.auth_secret')}</span>
              <input
                type="password"
                value={authSecret}
                onChange={(e) => setAuthSecret(e.target.value)}
              />
            </label>

            <Button type="submit" variant="primary" loading={loading}>
              {t('funil:conectar_wp.botao_salvar_testar')}
            </Button>
          </form>
        )}

        {step === 2 && (
          <div className={styles.card}>
            <h2 className={styles.stepTitle}>{t('funil:conectar_wp.passo2_titulo')}</h2>

            {loading && (
              <div className={styles.loadingBox}>{t('funil:conectar_wp.carregando_teste')}</div>
            )}

            {!loading && (
              <div className={styles.actions}>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => siteId && void executarTeste(siteId)}
                >
                  {t('funil:conectar_wp.botao_repetir_teste')}
                </Button>

                <Button type="button" variant="ghost" onClick={() => setStep(1)}>
                  {t('funil:conectar_wp.passo1_titulo')}
                </Button>
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div className={styles.card}>
            <h2 className={styles.stepTitle}>{t('funil:conectar_wp.passo3_titulo')}</h2>
            <p>{t('funil:conectar_wp.conclusao_mensagem')}</p>

            <div className={styles.actions}>
              <Button type="button" variant="primary" onClick={() => navigate('/dashboard')}>
                {t('funil:conectar_wp.ir_para_painel')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
